import { Link, useLocation } from "react-router-dom";
import type { ReportSummary } from "@/api/types";
import { Highlight } from "@/components/Highlight";
import { StatusBadge } from "@/components/StatusBadge";
import { dateRange, departmentLabel, money, relativeTime } from "@/format";

/**
 * A report in a list: who it's from, their manager and department, then the report itself, its
 * dates, size and where it stands. When it came from a search, the matching text is marked, and
 * merchants or categories that matched are listed since the card doesn't otherwise show them.
 */
export function ReportCard({
  report,
  showSubmitter = false,
  highlight,
}: {
  report: ReportSummary;
  showSubmitter?: boolean;
  highlight?: string | undefined;
}) {
  // The report's back link returns to this list, search included.
  const location = useLocation();
  const count = `${String(report.entryCount)} expense${report.entryCount === 1 ? "" : "s"}`;
  const department = departmentLabel(report);
  const people = [
    report.manager ? `Manager: ${report.manager.displayName}` : "No manager",
    department,
  ].filter((part): part is string => Boolean(part));
  // "Merchant: SHI, SHI International Corp.", one line per kind of match, in the order found.
  const matches = new Map<string, string[]>();
  for (const match of report.matches ?? []) {
    matches.set(match.field, [...(matches.get(match.field) ?? []), match.text]);
  }
  return (
    <Link
      className="report-card card"
      to={`/reports/${report.id}`}
      state={{ from: location.pathname + location.search }}
    >
      <div className="report-card-top">
        <strong className="report-card-title">
          <Highlight text={report.submitter.displayName} term={highlight} />
        </strong>
        <span className="report-card-amount">{money(report.totalUsd)}</span>
      </div>
      <span className="muted report-card-people">
        {people.map((part, index) => (
          <span key={part}>
            {index > 0 && " · "}
            <Highlight text={part} term={highlight} />
          </span>
        ))}
      </span>
      <span className="report-card-name">
        <Highlight text={report.title} term={highlight} />
      </span>
      {[...matches].map(([field, texts]) => (
        <span key={field} className="small report-card-matches">
          <span className="muted">{field}: </span>
          {texts.map((text, index) => (
            <span key={text}>
              {index > 0 && ", "}
              <Highlight text={text} term={highlight} />
            </span>
          ))}
        </span>
      ))}
      <span className="muted">
        {dateRange(report.periodStart, report.periodEnd)} · {count}
      </span>
      <div className="report-card-bottom">
        <StatusBadge report={report} />
        <span className="muted small">
          {showSubmitter && report.submittedAt
            ? `Sent ${relativeTime(report.submittedAt)}`
            : `Updated ${relativeTime(report.updatedAt)}`}
        </span>
      </div>
      {report.number ? (
        <span className="report-card-id">
          <Highlight text={`Report #${String(report.number)}`} term={highlight} />
        </span>
      ) : null}
    </Link>
  );
}

export function ReportListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading reports">
      {[0, 1, 2].map((key) => (
        <div key={key} className="card">
          <div className="skeleton line" />
          <div className="skeleton line wide" />
          <div className="skeleton line short" />
        </div>
      ))}
    </div>
  );
}
